/**
 * Pure reconnect-policy helpers for hooks/useEcho — extracted so the backoff
 * and reset decisions can be unit-tested without a real socket, React, or
 * the i18n side-effect import that useEcho carries.
 */

export const BASE_DELAY_MS = 200
export const MAX_DELAY_MS = 30000

/**
 * Exponential backoff (200ms → 30s cap) plus 10% jitter. PURE: reconnects
 * are NEVER capped by attempt count — a socket that stays down for hours
 * (nightly router reboot, backend restart) must still recover when it comes
 * back, which the old 10-attempt hard cap made impossible.
 */
export function computeReconnectDelay(attempt: number): number {
  // Floor and guard the input: a fractional/NaN/Infinity attempt would produce
  // a NaN/Infinity delay that defeats the backoff entirely.
  const safeAttempt = Number.isFinite(attempt) ? Math.max(0, Math.floor(attempt)) : 0
  const delay = Math.min(BASE_DELAY_MS * Math.pow(2, safeAttempt), MAX_DELAY_MS)
  const jitter = delay * 0.1 * Math.random()
  return delay + jitter
}

export type ReconnectResetEvent =
  | 'socket-connected'
  | 'browser-online'
  | 'document-visible'
  | 'other'

/**
 * PURE decision of when the reconnect attempt counter resets. The counter
 * resets when the socket actually reaches 'connected', and additionally on a
 * browser 'online' event or the document becoming visible WHILE the socket
 * is down: both are the moments a user comes back to a terminal that sat
 * disconnected, and the stale attempt count must not make recovery slower.
 */
export function shouldResetReconnectAttempts(event: ReconnectResetEvent, isSocketConnected: boolean): boolean {
  if (event === 'socket-connected') return true
  if (event === 'browser-online' || event === 'document-visible') return !isSocketConnected
  return false
}
